/** Zweck: Ermöglicht die Suche und Auswahl eines Ziels für Read-only-Impersonation. */
import {Component, OnDestroy, OnInit} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {Subject, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {AppUserService, ImpersonationTarget} from '../../services/app.user.service';

/** Verwaltet Suchergebnisse, Seitenwechsel und den Start der Read-only-Impersonation. */
@Component({
  selector: 'app-impersonate-target-picker',
  templateUrl: './impersonate-target-picker.component.html',
  styleUrls: ['./impersonate-target-picker.component.scss'],
  standalone: false
})
export class ImpersonateTargetPickerComponent implements OnInit, OnDestroy {
  displayedColumns = ['usertype', 'customerobject', 'fullname', 'email', 'lastseen'];
  search = '';
  page = 1;
  targets: ImpersonationTarget[] = [];
  loading = false;
  error = '';
  private request?: Subscription;
  private readonly searchChanges = new Subject<void>();
  private searchSubscription?: Subscription;

  /** Injiziert die Suche und den Dialogverweis. */
  constructor(private readonly userService: AppUserService,
              private readonly dialogRef: MatDialogRef<ImpersonateTargetPickerComponent>) {}

  /** Lädt beim Öffnen die ersten passenden Benutzer. */
  ngOnInit(): void {
    this.searchSubscription = this.searchChanges.pipe(debounceTime(250)).subscribe(() => this.loadTargets());
    this.loadTargets();
  }

  /** Lädt die aktuelle Suchseite und verwirft eine vorherige Anfrage. */
  loadTargets(): void {
    this.request?.unsubscribe();
    this.loading = true;
    this.error = '';
    this.request = this.userService.searchImpersonationTargets(this.search.trim(), this.page).subscribe({
      next: targets => {
        this.targets = targets;
        this.loading = false;
      },
      error: () => {
        this.targets = [];
        this.loading = false;
        this.error = 'Die Benutzerliste konnte nicht geladen werden.';
      }
    });
  }

  /** Setzt die Seitennummer bei einer Suche zurück. */
  onSearch(value: string): void {
    this.search = value;
    this.page = 1;
    this.request?.unsubscribe();
    this.loading = true;
    this.error = '';
    this.searchChanges.next();
  }

  /** Öffnet die nächste Seite, wenn die aktuelle Seite vollständig belegt ist. */
  nextPage(): void {
    if (this.targets.length === 25) {
      this.page++;
      this.loadTargets();
    }
  }

  /** Wechselt zurück zur vorherigen Ergebnisseite. */
  previousPage(): void {
    if (this.page > 1) {
      this.page--;
      this.loadTargets();
    }
  }

  /** Startet die Sitzung für den ausgewählten Benutzer und schließt den Dialog. */
  selectTarget(target: ImpersonationTarget): void {
    this.dialogRef.close();
    this.userService.startReadOnlyImpersonation(target);
  }

  /** Beendet die laufende Suche beim Schließen des Dialogs. */
  ngOnDestroy(): void {
    this.request?.unsubscribe();
    this.searchSubscription?.unsubscribe();
    this.searchChanges.complete();
  }
}
